import { useNavigation } from '@react-navigation/native'
import { observer } from 'mobx-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ImageBackground, StyleSheet, View } from 'react-native'
import { Text } from 'react-native-paper'
import { composeStyle } from '@linqpal/common-frontend/src/helpers'
import { commonColors } from '@linqpal/common-frontend/src/theme'
import { dictionaries } from '../../../../../models'
import { statusBadge } from '@linqpal/models/src/dictionaries/invoiceStatus'
import { Spacer } from '../../../ui/atoms'
import { BButton } from '../../../ui/atoms/builder-2.0/Button'
import { Status } from '../../../ui/atoms/Status'
import { getDisplayAmount } from '../../../utils/helpers/getDisplayAmount'
import { paths } from '../../links'
import { UploadInvoiceButton } from '../InvoicesTab/UploadInvoiceButton'
import { CardExpiredMessage } from './CardExpiredMessage'
import CreditStore from './CreditStore'
import { VirtualCardListing } from './VirtualCardListing'
import VirtualImage from '../../../assets/images/virtualCard.png'
import isArray from 'lodash/isArray'
import { dateMask } from '../../../utils/helpers/masking'

interface PropTypes {
  style?: any
  mobileView?: boolean
  data: any
}

export const PayNowWithVirtualCard = observer(
  ({ style, mobileView = false, data }: PropTypes) => {
    const { isExpired: isCardExpired } = CreditStore
    const { t } = useTranslation('global')
    const navigation = useNavigation()

    const isExpired = useMemo(
      () => isCardExpired(data.card?.status),
      [data, isCardExpired],
    )

    const getInvoiceId = (inv: string[] | string) => {
      return isArray(inv) ? inv[0] : inv
    }

    const getButton = () => {
      let button
      if (isExpired) {
        button = (
          <UploadInvoiceButton navigation={navigation}>
            <View style={styles.uploadButton}>
              <Text style={styles.buttonText}>
                {t('PayNowWithVirtualCard.upload-new-invoice')}
              </Text>
            </View>
          </UploadInvoiceButton>
        )
      } else {
        button = (
          <BButton
            buttonStyle={{ marginTop: 12, width: '100%', borderRadius: 4 }}
            contentStyle={{ height: 41 }}
            onPress={() =>
              navigation.navigate(paths.Console.Credit._self, {
                screen: paths.Console.Credit.VirtualCard,
                params: { id: getInvoiceId(data.invoiceDetails.invoiceId) },
              })
            }
          >
            {t('PayNowWithVirtualCard.see-card-details')}
          </BButton>
        )
      }
      return button
    }

    return (
      <View
        style={composeStyle(
          styles.rootView,
          {
            minHeight: mobileView && 195,
          },
          style,
        )}
      >
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text
            numberOfLines={1}
            ellipsizeMode="tail"
            style={{
              maxWidth: 130,
              fontWeight: '500',
              fontSize: 16,
              lineHeight: 24,
              color: commonColors.textDark,
            }}
          >
            {data?.invoices[0]?.supplierInvitationDetails?.name ||
              data?.company?.name}
          </Text>
          <Text
            style={{
              fontWeight: '500',
              fontSize: 16,
              lineHeight: 24,
              color: '#003353',
            }}
          >
            $ {getDisplayAmount(data?.approvedAmount)}
          </Text>
        </View>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text
            style={{
              fontWeight: '500',
              fontSize: 12,
              lineHeight: 20,
              color: commonColors.textLight,
            }}
          >
            {`Due on ${dateMask(data.invoices[0]?.invoice_due_date || '')}`}
          </Text>
          {isExpired && (
            <Status
              label={statusBadge[dictionaries.invoiceStatus.expired]?.label}
              // eslint-disable-next-line i18next/no-literal-string
              textColor="#F25A68"
            />
          )}
        </View>
        <Spacer height={14} />
        {isExpired && (
          <>
            <CardExpiredMessage />
            <Spacer height={14} />
          </>
        )}
        {!isExpired && (
          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <View>
              <ImageBackground
                source={{ uri: VirtualImage }}
                // eslint-disable-next-line i18next/no-literal-string
                resizeMode="contain"
                style={styles.imageBackground}
              >
                <Text
                  style={{
                    position: 'absolute',
                    left: 6,
                    top: 24,
                    fontWeight: '700',
                    fontSize: 9,
                    color: 'white',
                  }}
                >
                  {data.cbwCardInfo?.cardNumber?.slice(12)}
                </Text>
              </ImageBackground>
            </View>
            <Text
              style={{
                fontWeight: '500',
                fontSize: 14,
                lineHeight: 18,
                color: '#003353',
                alignSelf: 'center',
                marginLeft: 13,
              }}
            >
              {t('PayNowWithVirtualCard.message')}
            </Text>
          </View>
        )}
        {getButton()}
      </View>
    )
  },
)

export const ReadytoPayWithVirtualCard = ({
  mobileView = false,
  dontShowExpired = false,
}) => {
  const { t } = useTranslation('global')
  return (
    <View style={styles.rootView2}>
      <Text style={styles.heading}>
        {t('PayNowWithVirtualCard.ready-to-pay')}
      </Text>
      <Spacer height={20} />
      <VirtualCardListing
        spacer={13}
        dontShowExpired={dontShowExpired}
        mobileView={mobileView}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  rootView: {
    //minHeight: 195,
    marginHorizontal: 5,
    borderRadius: 8,
    borderColor: '#E6EBEE',
    borderWidth: 1,
    alignSelf: 'center',
    shadowColor: 'rgb(21, 41, 82)',
    backgroundColor: 'white',
    shadowOpacity: 0.03,
    shadowRadius: 20,
    paddingHorizontal: 20,
    paddingTop: 19,
    paddingBottom: 17,
  },
  rootView2: {
    minHeight: 282,
    backgroundColor: '#F4F9FD',
    paddingHorizontal: 20,
    paddingVertical: 25,
    marginHorizontal: -16,
    marginBottom: 20,
  },
  heading: {
    fontWeight: '600',
    fontSize: 16,
    lineHeight: 22,
  },
  uploadButton: {
    height: 41,
    backgroundColor: commonColors.accentText,
    width: '100%',
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 24,
    color: 'white',
  },
  imageBackground: {
    padding: 25,
  },
})
